import { Avatar, IconButton, Menu, MenuItem } from '@affine/component';
import { useSignOut } from '@affine/core/components/hooks/affine/use-sign-out';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { WorkspaceAvatar } from '@affine/core/components/workspace-avatar';
import { AuthService, DefaultServerService } from '@affine/core/modules/cloud';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import {
  type WorkspaceMetadata,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  AccountIcon,
  ImportIcon,
  MoreHorizontalIcon,
  PlusIcon,
  SearchIcon,
  SelfhostIcon,
  SignOutIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import * as styles from './embedded-workspace-list.css';

type EmbeddedWorkspaceListProps = {
  className?: string;
  onClickWorkspace?: (workspace: WorkspaceMetadata) => void;
  onCreatedWorkspace?: (payload: {
    metadata: WorkspaceMetadata;
    defaultDocId?: string;
  }) => void;
  showDiscoverAction?: boolean;
};

const EmbeddedWorkspaceRow = ({
  workspace,
  onClick,
}: {
  workspace: WorkspaceMetadata;
  onClick?: (workspace: WorkspaceMetadata) => void;
}) => {
  const workspaceInfo = useWorkspaceInfo(workspace);
  const name = workspaceInfo?.name ?? workspace.id;

  return (
    <li className={styles.workspaceListItem}>
      <button
        className={styles.workspaceButton}
        data-testid={`embedded-workspace-${workspace.id}`}
        onClick={() => onClick?.(workspace)}
        type="button"
      >
        <WorkspaceAvatar
          colorfulFallback
          meta={workspace}
          name={name}
          rounded={4}
          size={28}
        />
        <span className={styles.workspaceName}>{name}</span>
      </button>
    </li>
  );
};

export const EmbeddedWorkspaceList = ({
  className,
  onClickWorkspace,
  onCreatedWorkspace,
  showDiscoverAction = true,
}: EmbeddedWorkspaceListProps) => {
  const authService = useService(AuthService);
  const globalDialogService = useService(GlobalDialogService);
  const defaultServerService = useService(DefaultServerService);
  const workspacesService = useService(WorkspacesService);
  const session = useLiveData(authService.session.session$);
  const account = useLiveData(authService.session.account$);
  const workspaces = useLiveData(workspacesService.list.workspaces$);
  const navigate = useNavigate();
  const t = useI18n();
  const openSignOutModal = useSignOut();

  const isAuthenticated = session.status === 'authenticated';
  const canCreateLocalWorkspace =
    BUILD_CONFIG.isNative ||
    defaultServerService.server.config$.value.features.includes(
      ServerFeature.LocalWorkspace
    );

  const onSignIn = useCallback(() => {
    track.$.navigationPanel.workspaceList.requestSignIn();
    globalDialogService.open('sign-in', {});
  }, [globalDialogService]);

  const onNewWorkspace = useCallback(() => {
    if (!isAuthenticated && !canCreateLocalWorkspace) {
      onSignIn();
      return;
    }

    track.$.navigationPanel.workspaceList.createWorkspace();
    globalDialogService.open('create-workspace', {}, payload => {
      if (payload) {
        onCreatedWorkspace?.(payload);
      }
    });
  }, [
    canCreateLocalWorkspace,
    globalDialogService,
    isAuthenticated,
    onCreatedWorkspace,
    onSignIn,
  ]);

  const onAddWorkspace = useCallback(() => {
    track.$.navigationPanel.workspaceList.createWorkspace({
      control: 'import',
    });
    globalDialogService.open('import-workspace', undefined, payload => {
      if (payload) {
        onCreatedWorkspace?.({ metadata: payload.workspace });
      }
    });
  }, [globalDialogService, onCreatedWorkspace]);

  const onAddServer = useCallback(() => {
    globalDialogService.open('sign-in', { step: 'addSelfhosted' });
  }, [globalDialogService]);

  const onDiscover = useCallback(() => {
    navigate('/discover');
  }, [navigate]);

  return (
    <div
      className={clsx(styles.root, className)}
      data-testid="embedded-workspace-selector"
    >
      <div className={styles.account}>
        {isAuthenticated && account ? (
          <>
            <Avatar
              name={account.label}
              rounded={50}
              size={28}
              url={account.avatar}
            />
            <div className={styles.accountInfo}>
              <span className={styles.accountName}>{account.label}</span>
              {account.email ? (
                <span className={styles.accountEmail}>{account.email}</span>
              ) : null}
            </div>
            <Menu
              items={
                <MenuItem
                  data-testid="embedded-workspace-sign-out-option"
                  onClick={openSignOutModal}
                  prefixIcon={<SignOutIcon />}
                  type="danger"
                >
                  {t['com.affine.workspace.cloud.account.logout']()}
                </MenuItem>
              }
            >
              <IconButton
                aria-label={t['com.affine.blocksuite.toolbar.tooltip.more']()}
                className={styles.accountMenuButton}
                data-testid="embedded-workspace-account-menu"
                icon={<MoreHorizontalIcon />}
                tooltip={t['com.affine.blocksuite.toolbar.tooltip.more']()}
              />
            </Menu>
          </>
        ) : (
          <button
            className={styles.accountButton}
            data-testid="cloud-signin-button"
            onClick={onSignIn}
            type="button"
          >
            <AccountIcon />
            <span>{t['com.affine.workspace.cloud.auth']()}</span>
          </button>
        )}
      </div>

      {workspaces.length ? (
        <ul className={styles.workspaceList}>
          {workspaces.map(workspace => (
            <EmbeddedWorkspaceRow
              key={workspace.id}
              onClick={onClickWorkspace}
              workspace={workspace}
            />
          ))}
        </ul>
      ) : null}

      <div className={styles.actions}>
        {showDiscoverAction ? (
          <button
            className={styles.actionButton}
            data-testid="discover-workspaces"
            onClick={onDiscover}
            type="button"
          >
            <SearchIcon />
            <span>{t['com.affine.discovery.title']()}</span>
          </button>
        ) : null}
        {BUILD_CONFIG.isElectron ? (
          <button
            className={styles.actionButton}
            data-testid="add-workspace"
            onClick={onAddWorkspace}
            type="button"
          >
            <ImportIcon />
            <span>{t['com.affine.workspace.local.import']()}</span>
          </button>
        ) : null}
        {BUILD_CONFIG.isNative ? (
          <button
            className={styles.actionButton}
            data-testid="new-server"
            onClick={onAddServer}
            type="button"
          >
            <SelfhostIcon />
            <span>{t['com.affine.workspaceList.addServer']()}</span>
          </button>
        ) : null}
        <button
          className={styles.actionButton}
          data-testid="new-workspace"
          onClick={onNewWorkspace}
          type="button"
        >
          <PlusIcon />
          <span>
            {canCreateLocalWorkspace
              ? t['com.affine.workspaceList.addWorkspace.create']()
              : t['com.affine.workspaceList.addWorkspace.create-cloud']()}
          </span>
        </button>
      </div>
    </div>
  );
};
